/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { Feature } from 'ol';
import type { StyleLike } from 'ol/style/Style';
import React from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import type { layerTypes, CoreAppStore } from '@opengeoweb/store';
import {
  layerActions,
  drawingToolSelectors,
  layerUtils,
} from '@opengeoweb/store';
import type {
  DrawModeExitCallback,
  DrawModeValue,
  HoverSelect,
} from '@opengeoweb/webmap-react';
import {
  OpenLayersFeatureLayer,
  OpenLayersMapDraw,
  isGeoJSONGeometryEmpty,
} from '@opengeoweb/webmap-react';

interface SelectionState {
  geojson?: GeoJSON.FeatureCollection;
  selectedFeatureIds?: string[];
}

export const getCurrentSelectedIndices = ({
  geojson,
  selectedFeatureIds,
}: SelectionState): number[] => {
  if (!geojson?.features.length || !selectedFeatureIds?.length) {
    return [];
  }

  const selectedFeatureIdSet = new Set(selectedFeatureIds);
  return geojson.features.reduce<number[]>((indices, feature, index) => {
    const featureKey = layerUtils.getFeatureSelectionKey(feature);
    if (featureKey && selectedFeatureIdSet.has(featureKey)) {
      indices.push(index);
    }
    return indices;
  }, []);
};

export const calculateNewSelection = (
  currentSelectedIndices: number[],
  clickedIndex: number,
  isShiftClick: boolean,
): number[] => {
  if (isShiftClick) {
    if (currentSelectedIndices.includes(clickedIndex)) {
      return currentSelectedIndices.filter((i) => i !== clickedIndex);
    }
    return [...currentSelectedIndices, clickedIndex];
  }

  const isSoleSelection =
    currentSelectedIndices.length === 1 &&
    currentSelectedIndices[0] === clickedIndex;

  return isSoleSelection ? [] : [clickedIndex];
};

export const getAffectedIndices = (
  oldSelection: number[],
  newSelection: number[],
): number[] => {
  const allIndices = new Set([...oldSelection, ...newSelection]);
  return Array.from(allIndices).filter(
    (idx) => oldSelection.includes(idx) !== newSelection.includes(idx),
  );
};

export const shouldEnterEditMode = (
  isReadonly: boolean,
  isShiftClick: boolean,
  newSelectionCount: number,
): boolean => !isReadonly && !isShiftClick && newSelectionCount === 1;

interface OpenLayersFeatureLayerConnectProps {
  layer: layerTypes.ReduxLayer;
  layerId?: string;
  zIndex: number;
  style: StyleLike;
  hoverSelect?: HoverSelect;
}

export const OpenLayersFeatureLayerConnect: React.FC<
  OpenLayersFeatureLayerConnectProps
> = ({
  layer,
  layerId,
  zIndex,
  style,
  hoverSelect,
}: OpenLayersFeatureLayerConnectProps) => {
  const store = useStore<CoreAppStore>();
  const dispatch = useDispatch();
  const activeDrawToolId = useSelector(
    drawingToolSelectors.getActiveDrawToolId,
  );
  const activeDrawMode = useSelector((store: CoreAppStore) =>
    drawingToolSelectors.getActiveDrawMode(store, activeDrawToolId),
  );
  const shouldAllowMultipleShapes = useSelector((store: CoreAppStore) =>
    drawingToolSelectors.getShouldAllowMultipleShapes(store, activeDrawToolId),
  );
  const geoJSONIntersectionLayerId = useSelector((store: CoreAppStore) =>
    drawingToolSelectors.getGeoJSONIntersectionLayerId(store, activeDrawToolId),
  );
  const geoJSONIntersectionBoundsLayerId = useSelector((store: CoreAppStore) =>
    drawingToolSelectors.getGeoJSONIntersectionBoundsLayerId(
      store,
      activeDrawToolId,
    ),
  );
  const activeFeatureIndex = React.useMemo(() => {
    const currentActiveFeatureIndex = layerUtils.getSelectedFeatureIndexByIds(
      layer.geojson,
      layer.selectedFeatureIds,
    );

    if (
      !shouldAllowMultipleShapes ||
      !activeDrawMode?.selectionType ||
      !layer.geojson?.features.length
    ) {
      return currentActiveFeatureIndex ?? 0;
    }

    const currentSelectedFeature =
      currentActiveFeatureIndex !== undefined
        ? layer.geojson.features[currentActiveFeatureIndex]
        : undefined;

    if (
      currentSelectedFeature?.properties?.selectionType ===
      activeDrawMode.selectionType
    ) {
      return currentActiveFeatureIndex ?? 0;
    }

    let matchingFeatureIndex: number | undefined;

    for (
      let index = layer.geojson.features.length - 1;
      index >= 0;
      index -= 1
    ) {
      const feature = layer.geojson.features[index];

      if (feature.properties?.selectionType === activeDrawMode.selectionType) {
        if (isGeoJSONGeometryEmpty(feature)) {
          return index;
        }

        if (matchingFeatureIndex === undefined) {
          matchingFeatureIndex = index;
        }
      }
    }

    return matchingFeatureIndex ?? currentActiveFeatureIndex ?? 0;
  }, [
    activeDrawMode?.selectionType,
    layer.geojson,
    layer.selectedFeatureIds,
    shouldAllowMultipleShapes,
  ]);

  const onFeatureClick = React.useCallback(
    (feature: Feature[], isShiftClick?: boolean) => {
      const clickedIndex = layer.geojson!.features.findIndex(
        (feat) => feature[0] && feat.id === feature[0].getId(),
      );

      if (clickedIndex === -1) {
        return;
      }

      const clickedFeature = layer.geojson!.features[clickedIndex];
      const isReadonly = clickedFeature?.properties?.isReadonly === true;

      const currentSelection = getCurrentSelectedIndices({
        geojson: layer.geojson,
        selectedFeatureIds: layer.selectedFeatureIds,
      });

      // Keep a sole selected feature selected on normal click. Deselecting the
      // active edit target on click causes edit mode to collapse immediately;
      // clearing selection should instead happen via empty-space click or
      // explicit multi-select interactions.
      const shouldPreventDeselect =
        !isShiftClick &&
        currentSelection.length === 1 &&
        currentSelection[0] === clickedIndex;

      const newSelection = shouldPreventDeselect
        ? [clickedIndex]
        : calculateNewSelection(
            currentSelection,
            clickedIndex,
            isShiftClick ?? false,
          );

      const nextSelectedFeatureIds = newSelection
        .map((index) =>
          layerUtils.getFeatureSelectionKey(layer.geojson!.features[index]),
        )
        .filter((featureId): featureId is string => Boolean(featureId));

      dispatch(
        layerActions.setSelectedFeatures({
          layerId: layerId!,
          selectedFeatureIds: nextSelectedFeatureIds,
        }),
      );

      if (newSelection.length === 0) {
        dispatch(
          layerActions.toggleFeatureMode({
            layerId: layerId!,
            isInEditMode: false,
            drawMode: '',
          }),
        );
      }

      const affectedIndices = getAffectedIndices(
        currentSelection,
        newSelection,
      );
      affectedIndices.forEach((idx) => {
        const isNowSelected = newSelection.includes(idx);
        dispatch(
          layerActions.updateFeatureProperties({
            layerId: layerId!,
            properties: {
              highlightSelectedFeature: isNowSelected,
              isSelected: isNowSelected,
            },
            targetFeatureIndex: idx,
          }),
        );
      });

      if (
        shouldEnterEditMode(
          isReadonly,
          isShiftClick ?? false,
          newSelection.length,
        )
      ) {
        dispatch(
          layerActions.toggleFeatureMode({
            layerId: layerId!,
            isInEditMode: true,
            drawMode: '',
          }),
        );
      }
    },
    [dispatch, layer.geojson, layer.selectedFeatureIds, layerId],
  );

  const onNoFeatureClick = React.useCallback((): void => {
    if (activeDrawToolId) {
      // do not disable edit mode when a drawing tool is active
      return;
    }
    dispatch(
      layerActions.toggleFeatureMode({
        layerId: layerId!,
        isInEditMode: false,
        drawMode: '',
      }),
    );
  }, [activeDrawToolId, dispatch, layerId]);

  const onViewOnlyFeatureClick = React.useCallback(
    (feature: Feature[]): void => {
      if (activeDrawToolId) {
        return;
      }
      onFeatureClick(feature);
    },
    [activeDrawToolId, onFeatureClick],
  );

  const updateFeature = React.useCallback(
    (geojson: GeoJSON.FeatureCollection, reason: string): void => {
      if (!layerId) {
        window.console.error('updateFeature without layerId');
        return;
      }

      dispatch(
        layerActions.updateFeature({
          geojson,
          reason,
          layerId,
          shouldAllowMultipleShapes,
          geoJSONIntersectionLayerId,
          geoJSONIntersectionBoundsLayerId,
          selectionType: activeDrawMode?.selectionType,
        }),
      );
    },
    [
      layerId,
      dispatch,
      shouldAllowMultipleShapes,
      geoJSONIntersectionLayerId,
      geoJSONIntersectionBoundsLayerId,
      activeDrawMode?.selectionType,
    ],
  );

  const exitFeatureDrawMode = React.useCallback(
    (reason: DrawModeExitCallback): void => {
      if (!layerId) {
        window.console.error('exitFeatureDrawMode without layerId');
        return;
      }
      const activeDrawToolId = drawingToolSelectors.getActiveDrawToolId(
        store.getState(),
      );

      dispatch(
        layerActions.exitFeatureDrawMode({
          reason,
          layerId,
          shouldAllowMultipleShapes:
            drawingToolSelectors.getShouldAllowMultipleShapes(
              store.getState(),
              activeDrawToolId,
            ),
        }),
      );
    },
    [dispatch, store, layerId],
  );

  return (
    <>
      <OpenLayersFeatureLayer
        clickOnFeature={
          layer.isSelectable
            ? {
                onClick: onFeatureClick,
                hitTolerance: 5,
              }
            : undefined
        }
        featureCollection={!layer.isInEditMode ? layer.geojson : undefined}
        hoverSelect={hoverSelect}
        opacity={layer.opacity}
        style={style}
        visible={layer.enabled}
        zIndex={zIndex}
      />
      <OpenLayersMapDraw
        drawMode={layer.drawMode as DrawModeValue}
        exitDrawModeCallback={exitFeatureDrawMode}
        geojson={layer.geojson!}
        isInEditMode={layer.isInEditMode || false}
        onNoFeatureClick={onNoFeatureClick}
        onViewOnlyFeatureClick={onViewOnlyFeatureClick}
        selectedFeatureIndex={activeFeatureIndex}
        updateGeojson={updateFeature}
        viewOnlyStyle={style}
      />
    </>
  );
};
